import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WarningIcon from '@mui/icons-material/Warning'
import {
  BillingSettingsDto,
  billingService,
  UpdateBillingSettingsRequest,
} from '../api/adminServices'
import { ErrorCard, TableSkeleton } from '../components/Feedback'
import PageRefreshButton from '../components/PageRefreshButton'
import { useToast } from '../components/useToast'

const BILLING_DISABLED_ALERT =
  'Billing is OFF — the platform runs in free pilot mode. Subscription invoices are not generated, recurring charges are skipped, and accounts are not blocked for unpaid subscription fees. Use this while onboarding customers or during a promotional pilot period.'

const BILLING_ENABLED_ALERT =
  'Billing is ON — subscription billing is active across the platform. Recurring invoices are generated, payment collection runs on schedule, and accounts may enter past-due or suspension states when fees are unpaid.'

const ENABLE_CONFIRM_DESCRIPTION = (
  <Stack spacing={1.5}>
    <Typography variant="body2">
      You are about to <strong>enable subscription billing</strong> for the entire platform.
    </Typography>
    <Typography variant="body2" component="div">
      After enabling:
      <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
        <li>Recurring subscription invoices will be generated on billing dates.</li>
        <li>Payment collection and dunning flows become active.</li>
        <li>Accounts with unpaid fees may move to past-due or suspended status.</li>
        <li>The free pilot period ends for all accounts unless individually discounted.</li>
      </ul>
    </Typography>
    <Alert severity="warning" icon={<WarningAmberIcon />}>
      This is a platform-wide change. Confirm only when you are ready to start charging customers.
    </Alert>
  </Stack>
)

const DISABLE_CONFIRM_DESCRIPTION = (
  <Stack spacing={1.5}>
    <Typography variant="body2">
      You are about to <strong>disable subscription billing</strong> and return the platform to free pilot mode.
    </Typography>
    <Typography variant="body2" component="div">
      After disabling:
      <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
        <li>No new subscription invoices will be generated.</li>
        <li>Recurring charges and automatic collection are paused.</li>
        <li>Accounts will not be blocked or suspended solely for unpaid subscription fees.</li>
        <li>Existing subscriptions remain visible; usage limits still apply per plan.</li>
      </ul>
    </Typography>
    <Alert severity="info" icon={<CreditCardOffIcon />}>
      Use free pilot mode for onboarding, demos, or promotional periods. Re-enable billing when you are ready to collect fees.
    </Alert>
  </Stack>
)

function CutoverDetails({ cutover }: { cutover: BillingSettingsDto['cutover'] }) {
  if (!cutover) return null

  const entries = Object.entries(cutover).filter(([, value]) => value != null && value !== '')
  if (entries.length === 0) return null

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Last cutover result
      </Typography>
      <Stack spacing={0.75}>
        {entries.map(([key, value]) => (
          <Typography key={key} variant="body2" color="text.secondary">
            <strong>{key}:</strong>{' '}
            {Array.isArray(value) ? value.join(', ') : String(value)}
          </Typography>
        ))}
      </Stack>
    </Paper>
  )
}

export default function BillingSettingsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null)
  const [description, setDescription] = useState('')
  const [confirmPhraseTyped, setConfirmPhraseTyped] = useState('')
  const [formError, setFormError] = useState('')

  const { data, isLoading, isError, isFetching, error, refetch } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => billingService.get(),
  })

  const updateMutation = useMutation({
    mutationFn: (body: UpdateBillingSettingsRequest) => billingService.update(body),
    onSuccess: (result) => {
      queryClient.setQueryData(['billing-settings'], result)
      setPendingEnabled(null)
      setDescription('')
      setConfirmPhraseTyped('')
      setFormError('')
      toast.success(result.enabled ? 'Billing enabled' : 'Billing disabled')
    },
    onError: (e: any) => {
      setFormError(e?.response?.data?.message || e?.message || 'Failed to update billing settings')
    },
  })

  const enabled = data?.enabled ?? false
  const statusAlert = useMemo(
    () => (enabled ? BILLING_ENABLED_ALERT : BILLING_DISABLED_ALERT),
    [enabled],
  )

  const openConfirm = pendingEnabled !== null
  const confirmPhrase = pendingEnabled ? 'ENABLE BILLING' : 'DISABLE BILLING'
  const phraseMatches = confirmPhraseTyped.trim() === confirmPhrase
  const canConfirm =
    phraseMatches && description.trim().length > 0 && !updateMutation.isPending

  const handleToggleRequest = (nextEnabled: boolean) => {
    setFormError('')
    setDescription('')
    setConfirmPhraseTyped('')
    setPendingEnabled(nextEnabled)
  }

  const handleConfirm = () => {
    if (pendingEnabled === null) return
    const trimmed = description.trim()
    if (!trimmed) {
      setFormError('Please provide a reason for this change.')
      return
    }
    if (!phraseMatches) {
      setFormError('Confirmation phrase does not match.')
      return
    }
    updateMutation.mutate({ enabled: pendingEnabled, description: trimmed })
  }

  const closeConfirm = () => {
    if (updateMutation.isPending) return
    setPendingEnabled(null)
    setDescription('')
    setConfirmPhraseTyped('')
    setFormError('')
  }

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Billing control
        </Typography>
        <TableSkeleton rows={4} columns={1} />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Billing control
        </Typography>
        <ErrorCard message={(error as Error)?.message || 'Failed to load billing settings'} onRetry={() => refetch()} />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Billing control</Typography>
          <Typography variant="body2" color="text.secondary">
            Enable or disable platform-wide subscription billing for the free pilot period.
          </Typography>
        </Box>
        <PageRefreshButton loading={isFetching} onRefresh={() => refetch()} />
      </Stack>

      <Stack spacing={2.5}>
        <Alert
          severity={enabled ? 'warning' : 'info'}
          icon={enabled ? <CreditCardIcon /> : <CreditCardOffIcon />}
        >
          {statusAlert}
        </Alert>

        {data.persistError ? (
          <Alert severity="error">
            Settings could not be fully persisted: {data.persistError}
          </Alert>
        ) : null}

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h6">Subscription billing</Typography>
                <Typography variant="body2" color="text.secondary">
                  Default is disabled (free pilot). Toggle when you are ready to charge subscriptions.
                </Typography>
              </Box>
              <Chip
                label={enabled ? 'Enabled' : 'Disabled (free pilot)'}
                color={enabled ? 'warning' : 'default'}
                variant={enabled ? 'filled' : 'outlined'}
              />
            </Stack>

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(_, checked) => handleToggleRequest(checked)}
                  disabled={updateMutation.isPending}
                  color={enabled ? 'warning' : 'primary'}
                />
              }
              label={enabled ? 'Billing is enabled' : 'Billing is disabled'}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`Persisted: ${data.persisted ? 'Yes' : 'No'}`} />
              {data.version != null ? <Chip size="small" label={`Version: ${data.version}`} /> : null}
            </Stack>

            <CutoverDetails cutover={data.cutover} />
          </Stack>
        </Paper>

        {!enabled ? (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom>
              Free pilot mode is active
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customers can use subscription plans without being charged. No subscription invoices are issued and
              billing enforcement is paused. Enable billing when the pilot ends.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <Typography variant="subtitle2" gutterBottom>
              Live billing is active
            </Typography>
            <Typography variant="body2">
              Subscription fees are collected according to each account&apos;s plan and billing cycle. Monitor
              overdue invoices from Active Subscriptions and Finance.
            </Typography>
          </Paper>
        )}
      </Stack>

      <Dialog open={openConfirm} onClose={closeConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningIcon color={pendingEnabled ? 'warning' : 'error'} />
            <Typography component="span" variant="h6">
              {pendingEnabled ? 'Enable subscription billing?' : 'Disable subscription billing?'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {pendingEnabled ? ENABLE_CONFIRM_DESCRIPTION : DISABLE_CONFIRM_DESCRIPTION}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason for change"
              placeholder="e.g. Pilot period ended — starting paid billing"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value)
                if (formError) setFormError('')
              }}
              error={!!formError && !description.trim()}
              helperText="Required — recorded for audit purposes."
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Type{' '}
                <Box
                  component="code"
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                  }}
                >
                  {confirmPhrase}
                </Box>{' '}
                to confirm this platform-wide change.
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={confirmPhraseTyped}
                onChange={(e) => {
                  setConfirmPhraseTyped(e.target.value)
                  if (formError) setFormError('')
                }}
                placeholder={confirmPhrase}
                error={confirmPhraseTyped.length > 0 && !phraseMatches}
                helperText={
                  confirmPhraseTyped.length > 0 && !phraseMatches
                    ? 'Phrase does not match'
                    : undefined
                }
              />
            </Box>

            {formError ? <Alert severity="error">{formError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingEnabled ? 'warning' : 'error'}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {updateMutation.isPending
              ? 'Saving…'
              : pendingEnabled
                ? 'Enable billing'
                : 'Disable billing'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
